import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { ArrowUpRight, MessageCircle } from 'lucide-react'

import { supportService } from '@/app/services/supportService'
import { cn } from '@/app/utils/cn'
import { formatName } from '@/app/utils/formatName'
import masks from '@/app/utils/masks'
import { SupportCategoryEnum } from '@/app/utils/supportCategoryEnum'
import { buildWhatsappUrl } from '@/app/utils/whatsapp'
import { BasicAvatar } from '@/view/components/BasicAvatar'

import { RelatedCard } from './SupportSearchResults'

interface SupportSellerCardProps {
  sellerId?: string
  name?: string
  phone?: string
  onOpenSeller?: (sellerId: string) => void
  hasSeller: boolean
  isUnauthenticated: boolean
  isWhatsappDirect: boolean
}

const pluralize = (count = 0) =>
  `${count} ${count === 1 ? 'pendente' : 'pendentes'}`

export const SupportSellerCard = ({
  sellerId,
  name,
  phone,
  onOpenSeller,
  hasSeller,
  isUnauthenticated,
  isWhatsappDirect,
}: SupportSellerCardProps) => {
  const { data: person } = useQuery({
    queryKey: ['supportSeller', sellerId],
    queryFn: async () => {
      const response = await supportService.createSupport({
        search: name ?? '',
        categories: [SupportCategoryEnum.USER],
        saveHistory: false,
        limit: 50,
      })
      return response.sellers.find((seller) => seller._id === sellerId) ?? null
    },
    enabled: !!sellerId && !!name,
    staleTime: 5 * 60 * 1000,
  })

  const [team] = person?.teams ?? []
  const phoneLabel = phone ? masks.phone(phone) : undefined
  const sinceLabel = person?.createdAt
    ? `vendedor desde ${format(new Date(person.createdAt), 'MM/yyyy')}`
    : undefined

  const openSeller =
    sellerId && onOpenSeller ? () => onOpenSeller(sellerId) : undefined

  return (
    <div
      tabIndex={openSeller ? 0 : undefined}
      title={openSeller ? 'Abrir perfil do vendedor' : undefined}
      onClick={openSeller}
      onKeyDown={(event) => {
        if (event.key === 'Enter') openSeller?.()
      }}
      className={cn(
        'relative flex flex-col gap-3 rounded-xl border border-gray01 p-4',
        openSeller &&
          'cursor-pointer pr-10 transition-colors hover:border-black04 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
      )}
    >
      {openSeller && (
        <ArrowUpRight className="absolute right-3 top-3 h-4 w-4 text-black04" />
      )}
      <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
        Dados do vendedor
      </p>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <BasicAvatar
            name={name ?? ''}
            profileImageUrl={person?.profileImageUrl}
            style={{ backgroundColor: '#edeef0' }}
          />
          <div className="flex min-w-0 flex-col gap-1">
            <span className="truncate text-lg font-semibold text-black01">
              {name ? formatName(name) : 'Usuário não identificado'}
            </span>
            <span className="text-xs text-black04">
              {[phoneLabel, sinceLabel].filter(Boolean).join(' · ')}
            </span>
          </div>
        </div>

        {!!phone && (
          <a
            href={buildWhatsappUrl(phone)}
            target="_blank"
            rel="noreferrer"
            // o card inteiro abre o perfil; o WhatsApp não pode disparar os dois
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            className="flex h-9 items-center gap-2 rounded-lg border border-green-600 bg-white px-3 text-sm font-semibold text-green-700 transition-colors hover:bg-green-50"
          >
            <MessageCircle className="h-4 w-4" />
            Chamar no WhatsApp
          </a>
        )}
      </div>

      {!!person && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <RelatedCard
            title="Equipe"
            count={person.teamsCount ?? 0}
            main={team?.name ?? 'Sem equipe'}
            subtitle={
              team
                ? [
                    team.leaderName && `líder ${team.leaderName}`,
                    `${team.membersCount ?? 0} vendedores`,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : 'vendedor sem equipe'
            }
          />
          <RelatedCard
            title="Documentos"
            count={person.documentsTransactionsCount ?? 0}
            main={pluralize(person.documentsCount)}
          />
          <RelatedCard
            title="Propostas"
            count={person.transactionsCount ?? 0}
            main={pluralize(person.pendingTransactionsCount)}
          />
        </div>
      )}

      {isUnauthenticated && (
        <p className="text-xs text-black04">
          {isWhatsappDirect
            ? 'Aberto pelo WhatsApp, sem login'
            : 'Aberto pelo formulário público — dados informados pelo solicitante'}
          {!hasSeller && ' · sem cadastro vinculado'}
        </p>
      )}
    </div>
  )
}
